import {
  configurationReducer,
  type CatalogContext as CoreCatalogContext,
  type ConfigState as CoreConfigState,
  type Action as CoreAction,
} from "@apex/shared/config";
import type { WidgetState, WidgetAction } from "./types";
import { initialState } from "./types";

/**
 * Apply a core engine action and merge the resulting selectedItems back
 * into widget state. Widget doesn't surface pricingOverrides, so we always
 * pass empty Map and null snapshot — engine returns the same shape; we lift
 * the selectedItems Set out.
 */
function applyCoreAction(state: WidgetState, action: CoreAction): WidgetState {
  const ctx: CoreCatalogContext = state.selectedRootItem
    ? {
        rootItem: state.selectedRootItem,
        items: state.items,
        rules: state.itemRules,
        defaultItemIds: state.defaultItemIds,
      }
    : {
        // Fallback used only by callers dispatching TOGGLE_ITEM / SET_SELECTED_ITEMS
        // before ROOT_ITEM_LOADED. The engine uses defaultItemIds only for
        // APPLY_DEFAULTS (not routed through here), so this synthetic context
        // produces correct behaviour for the actions widget delegates.
        rootItem: { id: "__none__", code: "__none__", catalogItemTypeId: "__none__", displayOrder: 0 } as never,
        items: [],
        rules: [],
        defaultItemIds: [],
      };

  const coreState: CoreConfigState = {
    selectedItems: state.selectedItems,
    pricingOverrides: new Map(),
    originalSnapshot: null,
  };
  const next = configurationReducer(coreState, action, ctx);
  // Engine returns ReadonlySet; widget state holds Set. Wrap to match the wider type.
  return { ...state, selectedItems: new Set(next.selectedItems) };
}

/**
 * Widget state reducer
 *
 * Handles all state transitions for the widget. Uses a state machine pattern
 * where the current view determines valid transitions.
 *
 * TOGGLE_ITEM and SET_SELECTED_ITEMS delegate to the shared @apex/shared/config
 * engine so that selection logic stays in a single canonical place. All other
 * action types remain UI/view-state-local to the widget.
 */
export function widgetReducer(
  state: WidgetState,
  action: WidgetAction
): WidgetState {
  switch (action.type) {
    case "INIT":
      return {
        ...initialState,
        view: "loading",
        isLoading: true,
      };

    case "DATA_LOADED":
      return {
        ...state,
        view: "selection",
        rootItems: action.payload.rootItems,
        filters: action.payload.filters,
        rootHasPricing: action.payload.rootHasPricing,
        categories: action.payload.categories,
        isLoading: false,
        error: null,
      };

    case "SELECT_ROOT_ITEM":
      return {
        ...state,
        selectedRootItem: action.payload,
        isLoading: true,
      };

    case "ROOT_ITEM_LOADED": {
      // Build the CatalogContext from the payload now that all catalog data
      // is available, then route APPLY_DEFAULTS through the engine so the
      // canonical "seed defaults" path is engine-owned.
      const loadedCtx: CoreCatalogContext = {
        rootItem: action.payload.rootItem,
        items: action.payload.items,
        rules: action.payload.rules,
        defaultItemIds: action.payload.defaultItemIds,
      };
      const seedState: CoreConfigState = {
        selectedItems: new Set(),
        pricingOverrides: new Map(),
        originalSnapshot: null,
      };
      const afterDefaults = configurationReducer(seedState, { type: "APPLY_DEFAULTS" }, loadedCtx);
      return {
        ...state,
        view: "configure",
        selectedRootItem: action.payload.rootItem,
        items: action.payload.items,
        itemRules: action.payload.rules,
        defaultItemIds: action.payload.defaultItemIds,
        categories: action.payload.categories.length > 0
          ? action.payload.categories
          : state.categories,
        selectedItems: new Set(afterDefaults.selectedItems),
        // Reset accessory search — it targets the items just unloaded
        accessorySearchTerm: "",
        isLoading: false,
        error: null,
      };
    }

    case "TOGGLE_ITEM":
      return applyCoreAction(state, { type: "TOGGLE_ITEM", id: action.payload });

    case "SET_SELECTED_ITEMS":
      return applyCoreAction(state, { type: "SET_SELECTED_ITEMS", ids: action.payload });

    case "SET_FILTER":
      return {
        ...state,
        activeFilters: {
          ...state.activeFilters,
          [action.payload.key]: action.payload.value,
        },
      };

    case "SET_RANGE_FILTER":
      return {
        ...state,
        activeRangeFilters: {
          ...state.activeRangeFilters,
          [action.payload.key]: action.payload.range,
        },
      };

    case "CLEAR_RANGE_FILTER": {
      const next = { ...state.activeRangeFilters };
      delete next[action.payload];
      return {
        ...state,
        activeRangeFilters: next,
      };
    }

    case "CLEAR_FILTERS":
      return {
        ...state,
        activeFilters: {},
        activeRangeFilters: {},
        searchTerm: "",
      };

    case "SET_SEARCH_TERM":
      return {
        ...state,
        searchTerm: action.payload,
      };

    case "SET_ACCESSORY_SEARCH_TERM":
      return {
        ...state,
        accessorySearchTerm: action.payload,
      };

    case "SET_SORT_KEY":
      return {
        ...state,
        sortKey: action.payload,
      };

    case "UPDATE_QUOTE_FORM":
      return {
        ...state,
        quoteForm: {
          ...state.quoteForm,
          ...action.payload,
        },
      };

    case "SET_HONEYPOT":
      return {
        ...state,
        honeypot: action.payload,
      };

    case "VIEW_SUMMARY":
      // Only allow transition from configure view
      if (state.view !== "configure") {
        return state;
      }
      return {
        ...state,
        view: "summary",
      };

    case "SUBMIT_QUOTE":
      return {
        ...state,
        isSubmitting: true,
        error: null,
      };

    case "QUOTE_SUBMITTED":
      return {
        ...state,
        view: action.payload.success ? "success" : "error",
        isSubmitting: false,
        quoteResult: action.payload,
        error: action.payload.success
          ? null
          : new Error(action.payload.message || "Quote submission failed"),
      };

    case "BACK_TO_SELECTION":
      return {
        ...state,
        view: "selection",
        selectedRootItem: null,
        items: [],
        itemRules: [],
        defaultItemIds: [],
        selectedItems: new Set(),
        accessorySearchTerm: "",
        quoteForm: {},
        quoteResult: null,
        error: null,
      };

    case "BACK_TO_CONFIGURE":
      // Only allow from summary or error views
      if (state.view !== "summary" && state.view !== "error") {
        return state;
      }
      return {
        ...state,
        view: "configure",
        quoteResult: null,
        error: null,
      };

    case "RESET":
      return {
        ...initialState,
        // Keep loaded data
        rootItems: state.rootItems,
        filters: state.filters,
        categories: state.categories,
        view: "selection",
        isLoading: false,
      };

    case "SET_LOADING":
      return {
        ...state,
        isLoading: action.payload,
      };

    case "ERROR":
      return {
        ...state,
        view: "error",
        isLoading: false,
        isSubmitting: false,
        error: action.payload,
      };

    case "SERVICE_UNAVAILABLE":
      return {
        ...state,
        view: "service-unavailable",
        isLoading: false,
        isSubmitting: false,
        error: null,
        serviceUnavailable: action.payload,
      };

    case "HYDRATE":
      // Preserve loaded catalog data — only replace selection-shaped state.
      return {
        ...state,
        selectedItems: new Set(action.payload.selectedItemIds),
        view: action.payload.view,
        activeFilters: action.payload.activeFilters,
        activeRangeFilters:
          action.payload.activeRangeFilters ?? state.activeRangeFilters,
        searchTerm: action.payload.searchTerm ?? state.searchTerm,
        sortKey: action.payload.sortKey ?? state.sortKey,
      };

    default:
      return state;
  }
}

/**
 * Create initial state with optional overrides
 */
export function createInitialState(
  overrides?: Partial<WidgetState>
): WidgetState {
  return {
    ...initialState,
    ...overrides,
  };
}
